import { ref, nextTick } from 'vue'
import { useLang } from './useLang'
import { MessageType } from '../enum/chatEnum'
import { useChatStore } from '../store/chat'
import dayjs from 'dayjs'

// 乐观消息（本地先押入的"我说的话"）的 sender/username 需要跟当前视角一致，
// 否则客服视角下自己发的消息 sender='custom' 会被判成对方消息、气泡跑到左边。
// 抽成小工具让 sendMessage/sendImage/sendFile 三处共用，避免任一处漏改。
const mySenderOf = (role: string) => (role === 'agent' ? 'agent' : 'custom')

// 乐观消息的 senderId：客服视角下 isMe 判定为
// `sender==='agent' && String(senderId) === String(currentAgent)`，直接用 URL 里的 agentId（cStore.currentAgent）。
// 访客模式 senderId 不参与 isMe，空串即可。
const mySenderIdOf = (role: string, currentAgent: string) =>
  role === 'agent' ? String(currentAgent ?? '') : ''

export interface ChatImageUploadData {
  path: string
  preSignUrl: string
}

export interface ChatFileUploadResult {
  data: ChatImageUploadData
  fileInfo: {
    type: string
    isMedia: boolean
    duration: number
    size: number
    name: string
  }
}

/**
 * Owns Widget composer behavior and optimistic message creation.
 * Enter sends while Shift+Enter inserts a line break; IME composition suppresses sending until
 * composition ends. Text, image, and file sends append locally, signal a forced scroll, then send
 * through the existing chat store socket boundary.
 */
export function useChatInput() {
  const cStore = useChatStore()

  const textareaRef = ref<HTMLTextAreaElement>()
  const maxHeight = 98
  const minHeight = 32

  const isComposing = ref(false)

  const handleCompositionStart = () => {
    isComposing.value = true
  }

  const handleCompositionEnd = () => {
    isComposing.value = false
  }

  const { getUCLang } = useLang()

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      // 如果是输入法组合输入，忽略 Enter
      if (isComposing.value) {
        return
      }

      if (event.shiftKey) {
        return // 允许默认换行
      } else {
        event.preventDefault()
        sendMessage()
      }
    }
  }

  const autoResize = () => {
    nextTick(() => {
      const textarea = textareaRef.value
      if (!textarea) return

      textarea.style.height = 'auto'
      const contentHeight = textarea.scrollHeight
      const newHeight = Math.max(Math.min(contentHeight, maxHeight), minHeight)

      textarea.style.height = `${newHeight}px`
      textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden'
    })
  }

  const sendFile = (res: ChatFileUploadResult) => {
    if (!res.data.path) return

    const currentTime = dayjs().format('YYYY-MM-DD HH:mm:ss')

    const query = {
      chatId: cStore.chatId,
      messageType: MessageType.BINARY,
      language: getUCLang(),
      contentType: res.fileInfo.type,
      content: res.data.path,
      duration: res.fileInfo.isMedia ? res.fileInfo.duration.toString() : undefined,
      fileSize: res.fileInfo.size.toString(),
      fileName: res.fileInfo.name,
      timestamp: currentTime,
    }

    const thisChat = {
      chatId: cStore.chatId,
      avatar: '1',
      content: res.data.preSignUrl,
      contentType: res.fileInfo.type,
      sequenceId: 'unknown',
      timestamp: currentTime,
      sender: mySenderOf(cStore.role),
      senderId: mySenderIdOf(cStore.role, cStore.currentAgent),
      duration: res.fileInfo.isMedia ? res.fileInfo.duration.toString() : undefined,
      fileSize: res.fileInfo.size.toString(),
      fileName: res.fileInfo.name,
      username: mySenderOf(cStore.role),
    }

    // 本地存储
    cStore.chatInfo.push(thisChat)
    // 自己发消息：无视当前粘附态强制贴底
    cStore.scrollToBottomSignal++

    cStore.wsSend(query)

    cStore.chatContent = ''
    resetHeight()
  }

  const sendImage = (data: ChatImageUploadData) => {
    if (!data.path) return

    const currentTime = dayjs().format('YYYY-MM-DD HH:mm:ss')

    const query = {
      chatId: cStore.chatId,
      messageType: MessageType.BINARY,
      language: getUCLang(),
      contentType: 'image',
      content: data.path,
      timestamp: currentTime,
    }

    const thisChat = {
      chatId: cStore.chatId,
      avatar: '1', // 网页渠道客户默认头像
      content: data.preSignUrl,
      contentType: 'image',
      sequenceId: 'unknown',
      timestamp: currentTime,
      sender: mySenderOf(cStore.role),
      senderId: mySenderIdOf(cStore.role, cStore.currentAgent),
      username: mySenderOf(cStore.role),
    }

    // 本地存储
    cStore.chatInfo.push(thisChat)
    // 自己发消息：无视当前粘附态强制贴底
    cStore.scrollToBottomSignal++

    cStore.wsSend(query)

    cStore.chatContent = ''
    resetHeight()
  }

  /** Sends explicit text with an optimistic bubble and forced scroll, preserving the composer draft. */
  const sendTextMessage = (text: string): string | undefined => {
    const message = text.trim()
    if (!message) return

    const currentTime = dayjs().format('YYYY-MM-DD HH:mm:ss')

    const query = {
      chatId: cStore.chatId,
      messageType: MessageType.TEXT,
      language: getUCLang(),
      contentType: 'text/plain',
      content: message,
      timestamp: currentTime,
    }

    const thisChat = {
      chatId: cStore.chatId,
      avatar: '1', // 网页渠道客户默认头像
      content: message,
      contentType: 'text/plain',
      sequenceId: 'unknown',
      timestamp: currentTime,
      sender: mySenderOf(cStore.role),
      senderId: mySenderIdOf(cStore.role, cStore.currentAgent),
      username: mySenderOf(cStore.role),
    }

    // 本地存储
    cStore.chatInfo.push(thisChat)
    // 自己发消息：无视当前粘附态强制贴底
    cStore.scrollToBottomSignal++

    cStore.wsSend(query)

    return message
  }

  /** Sends the composer draft, clearing it and resetting its height only for a nonempty send. */
  const sendMessage = (): string | undefined => {
    const message = sendTextMessage(cStore.chatContent)
    if (!message) return
    cStore.chatContent = ''
    resetHeight()
    return message
  }

  const resetHeight = () => {
    nextTick(() => {
      const textarea = textareaRef.value
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.overflowY = 'hidden'
      }
    })
  }

  return {
    textareaRef,
    isComposing,
    handleKeydown,
    autoResize,
    sendMessage,
    sendFile,
    sendTextMessage,
    sendImage,
    handleCompositionStart,
    handleCompositionEnd,
  }
}
